import React, { useState, useEffect } from 'react';
import { X, FileText, Download, AlertCircle, Loader2 } from 'lucide-react';
import './ReceiptsModal.css';
import API_BASE_URL from '../../config/api';

const BASE_URL = API_BASE_URL;

const ReceiptsModal = ({ isOpen, onClose, userId }) => {
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    useEffect(() => {
        if (isOpen) {
            fetchReceipts();
        }
    }, [isOpen, userId]);

    const fetchReceipts = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const queryParam = userId ? `?userId=${userId}` : '';
            const response = await fetch(`${BASE_URL}/api/payment/moloni/receipts${queryParam}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setReceipts(data);
            } else {
                setError('Não foi possível carregar os recibos.');
            }
        } catch (err) {
            console.error('Error fetching receipts:', err);
            setError('Ocorreu um erro ao carregar os recibos.');
        } finally {
            setLoading(false);
        }
    };

    const handleViewPdf = async (docId, status) => {
        if (status?.toLowerCase() === 'rascunho') {
            alert('Não é possível descarregar o PDF de um documento em rascunho. O documento precisa de ser finalizado.');
            return;
        }

        setDownloadingId(docId);
        try {
            const token = localStorage.getItem('token');
            const queryParam = userId ? `&userId=${userId}` : '';
            const response = await fetch(`${BASE_URL}/api/payment/moloni/receipts/${docId}/pdf-link?${queryParam}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const { url } = await response.json();
                if (url) {
                    window.open(url, '_blank');
                } else {
                    alert('Link do PDF não disponível.');
                }
            } else {
                alert('Erro ao obter link do PDF.');
            }
        } catch (err) {
            console.error('Error getting PDF link:', err);
            alert('Ocorreu um erro ao tentar visualizar o PDF.');
        } finally {
            setDownloadingId(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="receipts-modal-overlay" onClick={onClose}>
            <div className="receipts-modal-container" onClick={(e) => e.stopPropagation()}>
                <div className="receipts-modal-header">
                    <h2>
                        <FileText size={24} style={{ color: 'var(--cdp-gold)' }} />
                        Os Meus Recibos
                    </h2>
                    <button className="close-modal-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="receipts-modal-content">
                    {loading ? (
                        <div className="receipts-loading-container">
                            <div className="receipts-spinner"></div>
                            <p>A procurar documentos...</p>
                        </div>
                    ) : error ? (
                        <div className="receipts-empty-state">
                            <AlertCircle size={48} style={{ color: '#ef4444', marginBottom: '16px' }} />
                            <h3>Ops!</h3>
                            <p>{error}</p>
                            <button className="view-pdf-btn" style={{ margin: '24px auto', width: 'auto', padding: '0 24px' }} onClick={fetchReceipts}>
                                Tentar novamente
                            </button>
                        </div>
                    ) : receipts.length === 0 ? (
                        <div className="receipts-empty-state">
                            <FileText size={48} style={{ color: 'var(--cdp-gold)', opacity: 0.5, marginBottom: '16px' }} />
                            <h3>Sem recibos encontrados</h3>
                            <p>Ainda não existem documentos sincronizados para esta conta.</p>
                        </div>
                    ) : (
                        <div className="receipts-table-container">
                            <table className="receipts-table">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Contribuinte</th>
                                        <th>Documento / Descrição</th>
                                        <th>Valor</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {receipts.map((receipt) => (
                                        <tr key={receipt.documentId} className="receipts-row">
                                            <td>{new Date(receipt.date).toLocaleDateString()}</td>
                                            <td>
                                                <div className="receipt-entity">{receipt.entityName || 'N/A'}</div>
                                                <div className="receipt-vat">NIF: {receipt.entityVat || '999999990'}</div>
                                            </td>
                                            <td>
                                                <div className="receipt-type">{receipt.documentType}</div>
                                                <div className="receipt-description">{receipt.notes || 'Quota Mensal'}</div>
                                                <span className={`receipt-status-badge ${receipt.status.toLowerCase()}`}>
                                                    {receipt.status}
                                                </span>
                                            </td>
                                            <td><span className="receipt-value">{receipt.grossValue.toFixed(2)}€</span></td>
                                            <td>
                                                <button
                                                    className="view-pdf-btn"
                                                    onClick={() => handleViewPdf(receipt.documentId, receipt.status)}
                                                    disabled={downloadingId === receipt.documentId}
                                                    title={receipt.status.toLowerCase() === 'rascunho' ? 'Rascunho (Não disponível)' : 'Descarregar PDF'}
                                                >
                                                    {downloadingId === receipt.documentId ? (
                                                        <Loader2 size={20} className="animate-spin" />
                                                    ) : (
                                                        <Download size={20} style={{ opacity: receipt.status.toLowerCase() === 'rascunho' ? 0.4 : 1 }} />
                                                    )}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReceiptsModal;
